<script setup lang="ts">
import animateElementsOnScroll from "@/js/intersection-observer.v2.js";
import glowImg from "@/assets/images/upgraded/glow.svg";
import api from "@/api";
import { DateInstance, SimplifiedDate } from "@/assets/js/dateHelper";

const isLoading = ref(true);
const isOver = ref(true);
const currentLocaleDate = ref();
const deadline = ref();

async function getUTCDateTime() {
	try {
		let res = await api.getUTCDateTime();

		currentLocaleDate.value = SimplifiedDate.fromUtcToLocal(
			res.data.res,
		).format("YYYY-MM-DD HH:mm:ss");
		deadline.value = DateInstance.getFormattedDate(
			"2025-06-15 23:59:59",
			"YYYY-MM-DD HH:mm:ss",
			false,
		);
		isOver.value = deadline.value <= currentLocaleDate.value;
	} catch (e) {
		console.log(e);
	} finally {
		isLoading.value = false;
	}
}

onMounted(async () => {
	await getUTCDateTime();

	animateElementsOnScroll(
		"#we-are-hiring",
		"slideFromBot 1s 0s forwards",
		".title, .job-responsibilities, .qualifications-needed, .salary-and-benefits, .other-benefits, .the-selection-process, .deadline-seaction, .we-are-hiring-container, .content-wrapper-alt",
	);

	const container = document.querySelector(".we-are-hiring-container");
	if (!container) return;

	const script = document.createElement("script");
	script.src = "https://www.cognitoforms.com/f/seamless.js";
	script.setAttribute("data-key", "YNA3aNVjv0Kf79LdM7RWRA");
	script.setAttribute("data-form", "28");

	container.appendChild(script);
	let mutationCount = 0;

	const observer = new MutationObserver((mutations, obs) => {
		for (const mutation of mutations) {
			if (mutation.addedNodes.length > 0) {
				mutationCount++;

				animateElementsOnScroll(
					"#we-are-hiring",
					"slideFromBot 1s 0s forwards",
					".we-are-hiring-container, .cog-cognito",
				);

				if (mutationCount > 1) {
					obs.disconnect();
					break;
				}
			}
		}
	});

	observer.observe(container, { childList: true, subtree: true });
});
</script>

<template>
	<div id="we-are-hiring">
		<template v-if="isLoading">
			<!-- <div class="loader"></div> -->
		</template>
		<template v-else>
			<template v-if="isOver">
				<div class="we-are-hiring-inner-wrapper">
					<inline-svg class="glow-img" :src="glowImg"></inline-svg>
					<h1 class="title">Applications are now closed</h1>
					<div
						class="content-wrapper content-wrapper-alt"
						style="max-width: 1000px; margin: 0 auto"
					>
						<div class="section-wrapper">
							<div class="text-alt">
								We would like to thank all candidates who applied for the
								Business Development Manager position.
							</div>
							<div class="text-alt">
								The application period closed on June 15, 2025. In the following
								days, all candidates will be contacted regarding the outcome of
								their application.
							</div>
							<div class="text-alt">
								We truly appreciate your interest and the time you invested in
								applying.
							</div>
							<div class="section-title-alt">
								Thank you for considering a future with us!
							</div>
						</div>
					</div>
				</div>
			</template>
			<template v-else>
				<div class="we-are-hiring-inner-wrapper">
					<inline-svg class="glow-img" :src="glowImg"></inline-svg>
					<h1 class="title">Business Development Manager - Application Form</h1>
					<div class="content-wrapper">
						<div class="section-wrapper job-responsibilities">
							<div class="section-title">Job responsibilities</div>
							<ul>
								<li>Achieving quarterly sales and profitability targets;</li>
								<li>
									Identifying and acquiring new clients, alongside strategic
									account development;
								</li>
								<li>
									Managing client relationships, taking part in project
									execution, and market research;
								</li>
								<li>
									Preparing and negotiating offers, ensuring smooth project
									delivery;
								</li>
								<li>
									Collaborating with internal teams to meet client needs
									efficiently;
								</li>
								<li>
									Representing the company at events, fairs, and client
									meetings;
								</li>
								<li>
									Ensuring profitability and adherence to pricing strategy;
								</li>
								<li>
									Supporting marketing initiatives and business process
									improvements.
								</li>
							</ul>
						</div>
						<div class="section-wrapper qualifications-needed">
							<div class="section-title">Qualifications needed</div>
							<ul>
								<li>
									At least 3 years’ experience in selling IT/telecoms or similar
									solutions;
								</li>
								<li>Bachelor/Masters degree is an advantage;</li>
								<li>Experience of working with foreign clients;</li>
								<li>
									Excellent communication, negotiation, presentation and
									interpersonal skills;
								</li>
								<li>
									Ability to think strategically and execute methodically;
								</li>
								<li>
									Willingness and ability to travel around Europe at short
									notice;
								</li>
								<li>Excellent English skills.</li>
							</ul>
						</div>
						<div class="section-wrapper salary-and-benefits">
							<div class="section-title">Salary and benefits</div>
							<div class="section-content">
								<div class="text">
									<span class="label">Net fixed salary per calendar month:</span
									><span>1,400 EUR</span>
								</div>
								<div class="text">
									<span class="label"
										>Net variable salary per calendar month (depending on
										delivery of KPIs):</span
									><span>up to additional 2,500 EUR + annual bonus</span>
								</div>
							</div>
						</div>

						<div class="section-wrapper other-benefits">
							<div class="section-title">Other benefits</div>
							<ul>
								<li>Flexible, hybrid work arrangement with 60% from office;</li>
								<li>Private medical insurance;</li>
								<li>
									Opportunities for international travel (all business expenses
									covered).
								</li>
							</ul>
						</div>

						<div class="section-wrapper the-selection-process">
							<div class="section-title">The selection process</div>
							<div class="section-content">
								<div class="text">
									There are 5 steps in the application process:
								</div>
								<ul>
									<li>Application form;</li>
									<li>On-line assessment;</li>
									<li>In-person interview;</li>
									<li>Experience day;</li>
									<li>Job offer;</li>
								</ul>
								<div class="text">
									The process may take between 30 and 45 days.
								</div>
							</div>
						</div>
						<div class="section-wrapper deadline-seaction">
							<div class="deadline">
								The deadline for application is June 15, 2025.
							</div>
						</div>
					</div>
					<div class="we-are-hiring-container"></div></div
			></template>
		</template>
	</div>
</template>

<style lang="scss" scoped>
#we-are-hiring {
	padding: 15vh 25px 5vh;
	min-height: 100vh;
	max-width: 1500px;
	margin: 0 auto;

	.we-are-hiring-inner-wrapper {
		position: relative;

		.glow-img {
			position: absolute;
			margin: 0 auto;
			width: 100%;
			max-width: 100%;
			top: 0;
			left: 0;
		}

		.title {
			text-align: center;
			font-style: normal;
			font-weight: 600;
			font-size: 32px;
			line-height: 40px;
			margin-bottom: 50px;
		}

		.content-wrapper {
			.section-wrapper {
				margin-bottom: 48px;

				.section-title {
					margin-bottom: 2rem;
					font-size: 28px;
					font-weight: 600;
					color: $app-color;
				}

				.deadline {
					font-size: 22px;
					font-weight: 600;
				}

				ul {
					padding-left: 16px;

					li {
						font-size: 15px;
					}
				}

				.section-content {
					.text {
						font-size: 15px;

						.label {
							font-weight: 700;
							margin-right: 4px;
						}
					}

					ul {
						padding-left: 32px;
					}
				}

				&.subsection-wrapper {
					margin-top: 2rem;
					padding-left: 16px;
				}

				.text-alt {
					margin-bottom: 2rem;
					font-size: 28px;
					font-weight: 600;
					text-align: center;
				}

				.section-title-alt {
					margin-bottom: 2rem;
					font-size: 28px;
					font-weight: 600;
					text-align: center;
					color: $app-color;
				}
			}
		}

		.we-are-hiring-container {
			max-width: 1500px;
			margin: 0 auto;
		}
	}
}

@media screen and (width >=768px) {
	#we-are-hiring {
		padding: 300px 25px 5vh;

		.we-are-hiring-inner-wrapper {
			.title {
				font-size: 54px;
				margin-bottom: 100px;
			}

			.content-wrapper {
				.section-wrapper {
					margin-bottom: 60px;

					ul {
						li {
							font-size: 18px;
						}
					}

					.section-content {
						.text {
							font-size: 18px;

							.label {
								font-weight: 700;
							}
						}
					}

					&.subsection-wrapper {
						margin-top: 60px;
					}
				}
			}
		}
	}

	.loader {
		border: 4px solid #f3f3f3;
		/* stylelint-disable-next-line declaration-property-value-no-unknown */
		border-top: 4px solid $app-color;
		border-radius: 50%;
		width: 40px;
		height: 40px;
		animation: spin 1s linear infinite;
		margin: auto;
	}

	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}
}
</style>
